import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './MemoryEnrichmentPage.css';

function MemoryEnrichmentPage({ apiUrl }) {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [pasteText, setPasteText] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [storyText, setStoryText] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!profileId || !token) return;
    try {
      const res = await axios.get(
        `${apiUrl}/personalization/enrichment-summary/${profileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load enrichment summary', err);
    }
  }, [apiUrl, profileId, token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleImportPaste = async () => {
    if (!pasteText.trim()) {
      setError('Please paste some text — memories, stories, phrases, or chat excerpts.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${apiUrl}/personalization/import-memories/${profileId}`,
        { text: pasteText, source_label: 'paste' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastImport(res.data);
      setSummary(res.data.summary);
      setPasteText('');
      await loadSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStory = async () => {
    if (!storyText.trim()) {
      setError('Please write your story.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${apiUrl}/personalization/freeform-story/${profileId}`,
        { story: storyText, title: storyTitle || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastImport(res.data);
      setSummary(res.data.summary);
      setStoryTitle('');
      setStoryText('');
      await loadSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save story.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrichment-page">
      <header className="enrichment-header">
        <button className="back-button" onClick={() => navigate('/personalization')}>
          ← Back
        </button>
        <h1>✨ Enrich Your Digital Twin</h1>
        <p>
          Paste memories, stories, or phrases — no interview needed. AI extracts what makes you sound like you.
        </p>
      </header>

      {stats && (
        <div className="stats-bar">
          <span>{stats.memory_count} memories</span>
          <span>{stats.phrase_count} signature phrases</span>
          <span>{stats.import_count} imports</span>
        </div>
      )}

      {error && (
        <div className="enrichment-error">
          ⚠️ {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {summary && (
        <div className="import-success">
          <strong>Latest import:</strong> {summary}
          {lastImport?.memories_added > 0 && (
            <span className="import-meta"> · {lastImport.memories_added} new memories added</span>
          )}
        </div>
      )}

      <div className="enrichment-grid">
        <section className="enrichment-card">
          <h2>📋 Paste anything</h2>
          <p className="card-hint">
            Old emails, text messages, voice memo transcripts, lists of nicknames, holiday traditions…
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste your memories, stories, or phrases here..."
            rows={10}
          />
          <button
            className="button-primary"
            onClick={handleImportPaste}
            disabled={loading || !pasteText.trim()}
          >
            {loading ? 'Extracting…' : 'Import & enrich profile'}
          </button>
        </section>

        <section className="enrichment-card">
          <h2>📖 Quick story</h2>
          <p className="card-hint">Share one memory in your own words — we'll extract the details.</p>
          <input
            type="text"
            value={storyTitle}
            onChange={(e) => setStoryTitle(e.target.value)}
            placeholder="Story title (optional)"
          />
          <textarea
            value={storyText}
            onChange={(e) => setStoryText(e.target.value)}
            placeholder="Tell a story you often share with your loved one..."
            rows={8}
          />
          <button
            className="button-primary"
            onClick={handleAddStory}
            disabled={loading || !storyText.trim()}
          >
            {loading ? 'Saving…' : 'Add story'}
          </button>
        </section>
      </div>

      {stats?.recent_memories?.length > 0 && (
        <section className="recent-memories">
          <h3>Recent memories</h3>
          <ul>
            {stats.recent_memories.map((m, i) => (
              <li key={i}>
                <strong>{m.title}</strong>
                {m.story && <p>{m.story}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="enrichment-footer">
        <button className="button-secondary" onClick={() => navigate(`/survey/${profileId}`)}>
          🎤 Continue voice interview
        </button>
      </div>
    </div>
  );
}

export default MemoryEnrichmentPage;
